<!-- src/views/pages/superadmin/companies/super-admin-company-table.vue -->
<script setup>
import { computed } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { useRouter } from "vue-router";
import { toast } from "@/utils/toast";

defineOptions({ name: "SuperAdminCompanyTable" });

const props = defineProps({
  companies: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["edit", "delete"]);

const authStore = useAuthStore();
const router = useRouter();

const loginAsCompany = async (record) => {
  if (!record?.id) return;

  try {
    await authStore.impersonateCompany(record.id);
    await router.push("/dashboard/admin-dashboard");
    toast("Logged in as company successfully.", "success");
  } catch (error) {
    toast(
      error?.response?.data?.message || "Failed to impersonate company.",
      "error"
    );
  }
};

// --- columns (kept intact) ---
const columns = [
  { sorter: false },
  {
    title: "Company Name",
    dataIndex: "Company_Name",
    key: "Company_Name",
    sorter: {
      compare: (a, b) => {
        const x = (a.Company_Name || "").toLowerCase();
        const y = (b.Company_Name || "").toLowerCase();
        return x > y ? -1 : y > x ? 1 : 0;
      },
    },
  },
  {
    title: "Email",
    dataIndex: "Email",
    sorter: {
      compare: (a, b) => {
        const x = (a.Email || "").toLowerCase();
        const y = (b.Email || "").toLowerCase();
        return x > y ? -1 : y > x ? 1 : 0;
      },
    },
  },
  {
    title: "Phone",
    dataIndex: "Phone",
    sorter: {
      compare: (a, b) => {
        const x = (a.Phone || "").toLowerCase();
        const y = (b.Phone || "").toLowerCase();
        return x > y ? -1 : y > x ? 1 : 0;
      },
    },
  },
  {
    title: "Location",
    dataIndex: "Location",
    sorter: {
      compare: (a, b) => {
        const x = (a.Location || "").toLowerCase();
        const y = (b.Location || "").toLowerCase();
        return x > y ? -1 : y > x ? 1 : 0;
      },
    },
  },
  {
    title: "Rating",
    dataIndex: "Rating",
    key: "Rating",
    sorter: {
      compare: (a, b) => {
        const x = (a.Rating || "").toString().toLowerCase();
        const y = (b.Rating || "").toString().toLowerCase();
        return x > y ? -1 : y > x ? 1 : 0;
      },
    },
  },
  {
    title: "Owner",
    dataIndex: "Owner",
    sorter: {
      compare: (a, b) => {
        const x = (a.Owner || "").toLowerCase();
        const y = (b.Owner || "").toLowerCase();
        return x > y ? -1 : y > x ? 1 : 0;
      },
    },
  },
  {
    title: "Contact",
    dataIndex: "Contact",
    key: "Contact",
    sorter: {
      compare: (a, b) => {
        const x = (a.Contact || "").toLowerCase();
        const y = (b.Contact || "").toLowerCase();
        return x > y ? -1 : y > x ? 1 : 0;
      },
    },
  },
  {
    title: "Status",
    dataIndex: "Status",
    key: "Status",
    sorter: {
      compare: (a, b) => {
        const x = (a.Status || "").toLowerCase();
        const y = (b.Status || "").toLowerCase();
        return x > y ? -1 : y > x ? 1 : 0;
      },
    },
  },
  { title: "", key: "action", sorter: true },
];

// --- row selection (kept intact) ---
const rowSelection = {
  onChange: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
};

// --- base url resolver (supports Vite + Vue CLI envs) ---
const baseUrl = (
  import.meta?.env?.VITE_BASE_URL ||
  process.env.VUE_APP_BASE_URL ||
  ""
).trim();

/**
 * Build a safe src for images.
 * Supports:
 *  - absolute: http://127.0.0.1:8000/storage/logo/...
 *  - root-relative: /storage/logo/...
 *  - storage-relative: logo/xxx.png  (stored in /storage/logo/xxx.png)
 */
function imgSrc(urlOrPath) {
  if (!urlOrPath) return "";
  const v = String(urlOrPath).trim();

  // Already absolute
  if (/^https?:\/\//i.test(v)) return v;

  const cleanBase = baseUrl.replace(/\/+$/, "");

  // Root-relative path
  if (v.startsWith("/")) return `${cleanBase}${v}`;

  // "logo/xxx.png" -> "/storage/logo/xxx.png"
  return `${cleanBase}/storage/${v.replace(/^\/+/, "")}`;
}

// --- data source mapping (logic intact + adds _logo_src for robustness) ---
const dataSource = computed(() => {
  return (props.companies || []).map((c) => {
    const location =
      c.country || (c.state ? `${c.state}` : "") || c.address || "";

    // if backend ever sends owner/rating, we can use them; for now fallback
    const owner =
      c.owner_name ||
      c.owner ||
      (c.users && c.users.length ? c.users[0].name || "" : "");

    return {
      ...c,
      key: c.id,
      id: c.id,
      Company_Name: c.company_name,
      Email: c.email,
      Phone: c.phone || c.phone_two,
      Location: location,
      Rating: c.rating || "",
      Owner: owner,
      Status: typeof c.status === "string" ? c.status : String(c.status),

      // ✅ preferred image source:
      // 1) backend absolute url (logo_url)
      // 2) fallback relative path (logo) -> /storage/{logo}
      _logo_src: imgSrc(c.logo_url || c.logo),
    };
  });
});

function onEdit(record) {
  emit("edit", record);
}

function onDelete(record) {
  emit("delete", record);
}
</script>

<template>
  <a-table
    class="table datatable thead-light"
    :columns="columns"
    :data-source="dataSource"
    :row-selection="rowSelection"
    :loading="loading"
  >
    <template #bodyCell="{ column, record }">
      <!-- Company Name + Logo -->
      <template v-if="column.key === 'Company_Name'">
        <div class="d-flex align-items-center file-name-icon">
          <router-link
            :to="`/super-admin/companies-details/${record.id}`"
            class="avatar avatar-md border rounded-circle"
          >
            <img
              v-if="record._logo_src"
              :src="record._logo_src"
              class="img-fluid"
              alt="img"
            />
            <span
              v-else
              class="d-flex align-items-center justify-content-center w-100 h-100 bg-light"
            >
              <i class="ti ti-building"></i>
            </span>
          </router-link>

          <div class="ms-2">
            <h6 class="fw-medium">
              <router-link :to="`/super-admin/companies-details/${record.id}`">
                {{ record.Company_Name }}
              </router-link>
            </h6>
          </div>
        </div>
      </template>

      <!-- Rating -->
      <template v-else-if="column.key === 'Rating'">
        <span v-if="record.Rating">
          <i class="ti ti-star-filled text-warning me-2"></i>{{ record.Rating }}
        </span>
        <span v-else>-</span>
      </template>

      <!-- Contact Icons -->
      <template v-else-if="column.key === 'Contact'">
        <ul class="contact-icon d-flex align-items-center">
          <li v-if="record.Email">
            <a
              :href="`mailto:${record.Email}`"
              class="p-1 rounded-circle contact-icon-mail d-flex align-items-center justify-content-center"
            >
              <span class="d-flex align-items-center justify-content-center">
                <i class="ti ti-mail text-gray-5"></i>
              </span>
            </a>
          </li>

          <li v-if="record.Phone">
            <a
              :href="`tel:${record.Phone}`"
              class="p-1 rounded-circle contact-icon-call d-flex align-items-center justify-content-center"
            >
              <span class="d-flex align-items-center justify-content-center">
                <i class="ti ti-phone-call text-gray-5"></i>
              </span>
            </a>
          </li>

          <li v-if="record.whatsapp_profile">
            <a
              :href="`https://wa.me/${record.whatsapp_profile}`"
              target="_blank"
              class="p-1 rounded-circle contact-icon-msg d-flex align-items-center justify-content-center"
              rel="noopener"
            >
              <span class="d-flex align-items-center justify-content-center">
                <i class="ti ti-message-2 text-gray-5"></i>
              </span>
            </a>
          </li>

          <li v-if="record.skype_profile">
            <a
              :href="`skype:${record.skype_profile}?chat`"
              class="p-1 rounded-circle contact-icon-skype d-flex align-items-center justify-content-center"
            >
              <span class="d-flex align-items-center justify-content-center">
                <i class="ti ti-brand-skype text-gray-5"></i>
              </span>
            </a>
          </li>

          <li v-if="record.facebook_profile">
            <a
              :href="record.facebook_profile"
              target="_blank"
              class="p-1 rounded-circle contact-icon-facebook d-flex align-items-center justify-content-center"
              rel="noopener"
            >
              <span class="d-flex align-items-center justify-content-center">
                <i class="ti ti-brand-facebook text-gray-5"></i>
              </span>
            </a>
          </li>
        </ul>
      </template>

      <!-- Status -->
      <template v-else-if="column.key === 'Status'">
        <span
          :class="[
            'badge',
            record.Status === 'Active' ? 'badge-success' : 'badge-danger',
            'd-inline-flex',
            'align-items-center',
            'badge-xs',
          ]"
        >
          <i class="ti ti-point-filled me-1"></i>{{ record.Status }}
        </span>
      </template>

      <!-- Actions -->
      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex">
          <router-link :to="`/super-admin/companies-details/${record.id}`" class="me-2">
            <i class="ti ti-eye"></i>
          </router-link>

          <a
            href="javascript:void(0);"
            class="me-2"
            title="Login as Company"
            @click.prevent="loginAsCompany(record)"
          >
            <i class="ti ti-login"></i>
          </a>

          <a
            href="javascript:void(0);"
            class="me-2"
            @click.prevent="onEdit(record)"
          >
            <i class="ti ti-edit"></i>
          </a>

          <a href="javascript:void(0);" @click.prevent="onDelete(record)">
            <i class="ti ti-trash"></i>
          </a>
        </div>
      </template>

      <!-- Default rendering for other columns -->
      <template v-else>
        {{ record[column.dataIndex] }}
      </template>
    </template>
  </a-table>
</template>
